import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Box, Container } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner, NewPageTitleBar, WcnButton } from '@/components';
import {
    FilterShopDateTimeInterval,
    FilterShopDateTimeIntervalData
} from '@/components/filters/FilterShopDateTimeInterval';
import { getMenuData } from '@core/data/menuData';
import { incidentsService, Incident } from '@/services/incidentsService';
import { FCSecurePage, useRegisterRefresh } from '@/components/shared/navigation/securePage.ts';
import BaseSelector from '@/components/selectors/BaseSelector';
import NewWcnTable, { DataTableColumn } from '@/components/datatable/NewWcnTable.tsx';
import { useMobileHeaderPanelContent } from '@layouts/MobileHeaderPanel.tsx';
import { useSettingsStore, useAuthStore } from '@core/store';
import { downloadBlob } from '@core/utils';
import { useNotifications } from '@core/hooks/useNotifications.ts';
import { eventBus, EventTypes } from '@core/pubsub/EventBus.ts';
import { DateTime } from 'luxon';
import { ActiveLicenses } from '@core/types';

const matchesSelectedTypes = (item: Incident, selectedTypes: string[]) =>
    selectedTypes.includes('OperationModeChanged')
        ? item.incidentType?.includes('OperationModeChanged') || selectedTypes.includes(item.incidentType)
        : selectedTypes.includes(item.incidentType);

const ShopAutomationIncidents: FCSecurePage = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [incidents, setIncidents] = useState<Incident[]>([]);
    const [selectedIncidentTypes, setSelectedIncidentTypes] = useState<string[]>([]);
    const [selectedDevices, setSelectedDevices] = useState<string[]>([]);
    const [eventSelection, setEventSelection] = useState({ allSelected: false, isIndeterminate: false });
    const [deviceSelection, setDeviceSelection] = useState({ allSelected: false, isIndeterminate: false });
    const [currentFilter, setCurrentFilter] = useState<FilterShopDateTimeIntervalData | null>(null);
    const [lastUpdate, setLastUpdate] = useState<DateTime | undefined>(undefined);
    const { notifyError, notifyErrorResponse } = useNotifications();
    const { isMobile } = useSettingsStore();
    const { hasLicense } = useAuthStore();

    const incidentTypes = useMemo(() => incidentsService.getUniqueIncidentTypes(incidents), [incidents]);
    const deviceOptions = useMemo(() => incidentsService.getUniqueIncidentDevices(incidents), [incidents]);

    const incidentTypeItems = useMemo(
        () => incidentTypes.map((type) => ({ value: type, text: type })),
        [incidentTypes]
    );

    const deviceItems = useMemo(
        () => deviceOptions.map((device) => ({ value: device, text: device })),
        [deviceOptions]
    );

    const tableColumns: DataTableColumn<Incident>[] = useMemo(
        () => [
            { text: t('event'), value: 'incidentType', width: 320 },
            {
                text: t('timestamp'),
                value: 'incidentTimestamp',
                width: 180,
                render: (incident) => incidentsService.formatIncidentTimestamp(incident.incidentTimestamp)
            },
            { text: t('source'), value: 'incidentDevice', width: 250 },
            ...(hasLicense(ActiveLicenses.ShopAutomation)
                ? [
                      {
                          text: t('operationMode'),
                          value: 'operationMode',
                          width: 200,
                          render: (incident: Incident) =>
                              !incident.operationMode || incident.operationMode === '-' ? (
                                  <i className="bi bi-dash-lg" style={{ color: 'var(--gray-medium)' }} />
                              ) : (
                                  incident.operationMode
                              )
                      } as DataTableColumn<Incident>
                  ]
                : []),
            {
                text: t('hadMailReceivers'),
                value: 'hasMailReceivers',
                width: 200,
                sortable: true,
                render: (incident) =>
                    incident.hasMailReceivers ? (
                        <i className="bi bi-check-square" style={{ color: 'var(--gray-medium)' }} />
                    ) : (
                        <i className="bi bi-dash-lg" style={{ color: 'var(--gray-medium)' }} />
                    )
            }
        ],
        [t, hasLicense]
    );

    const loadData = useCallback(
        async (args: FilterShopDateTimeIntervalData) => {
            setLoading(true);
            try {
                const data = await incidentsService.loadIncidents(args.shopId, args.start, args.end);
                setIncidents(data);
                setCurrentFilter(args);
                setLastUpdate(DateTime.now());
            } catch (error) {
                console.error('Failed to load incidents:', error);
                notifyError(t('errorLoadingData'));
            } finally {
                setLoading(false);
            }
        },
        [t]
    );

    const handleReload = useCallback(async () => {
        if (!currentFilter) return;
        await loadData({ shopId: currentFilter.shopId, start: currentFilter.start, end: currentFilter.end });
    }, [currentFilter, loadData]);

    useRegisterRefresh(handleReload);

    useEffect(() => {
        const unsubscribe = eventBus.on(EventTypes.WIDGETS_RELOAD, handleReload);
        return unsubscribe;
    }, [handleReload]);

    const handleIncidentTypeChange = useCallback((val: string[]) => {
        setSelectedIncidentTypes(val);
        setCurrentFilter((prev) => (prev ? { ...prev, event: val } : prev));
    }, []);

    const handleDeviceChange = useCallback((val: string[]) => {
        setSelectedDevices(val);
        setCurrentFilter((prev) => (prev ? { ...prev, source: val } : prev));
    }, []);

    const filteredIncidents = useMemo(() => {
        return incidents.filter((item) => {
            const typeMatch =
                selectedIncidentTypes.length === 0 ||
                eventSelection.allSelected ||
                matchesSelectedTypes(item, selectedIncidentTypes);
            const deviceMatch =
                selectedDevices.length === 0 ||
                deviceSelection.allSelected ||
                selectedDevices.includes(item.incidentDevice);
            return typeMatch && deviceMatch;
        });
    }, [incidents, selectedIncidentTypes, selectedDevices, eventSelection.allSelected, deviceSelection.allSelected]);

    const filterKey = useMemo(
        () => `${selectedIncidentTypes.join(',')}-${selectedDevices.join(',')}`,
        [selectedIncidentTypes, selectedDevices]
    );

    const handleExport = useCallback(async () => {
        if (!currentFilter) return;
        setLoading(true);
        try {
            const { shopId, start, end } = currentFilter;
            const selectedEvents = selectedIncidentTypes.length === 0 ? incidentTypes : selectedIncidentTypes;
            const selectedSources = selectedDevices.length === 0 ? deviceOptions : selectedDevices;
            const blob = await incidentsService.exportIncidents(shopId, start, end, selectedEvents, selectedSources);
            downloadBlob(blob, `Incident_${start}_${end}.csv`, blob.type);
        } catch (error) {
            notifyErrorResponse(error);
        } finally {
            setLoading(false);
        }
    }, [currentFilter]);

    const filterContent = useMemo(
        () => (
            <>
                <FilterShopDateTimeInterval onLoadData={loadData} />

                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ width: isMobile ? '100%' : 560, flex: isMobile ? '1 1 100%' : '0 0 560px' }}>
                        <BaseSelector
                            multiple
                            value={selectedIncidentTypes}
                            items={incidentTypeItems}
                            label={t('event')}
                            onChange={handleIncidentTypeChange}
                            onSelection={setEventSelection}
                            selectDisplayLabels={{
                                placeholderLabel: t('selectEvent'),
                                countLabel: t('selectedWithValue')
                            }}
                        />
                    </Box>

                    <Box sx={{ width: isMobile ? '100%' : 323, flex: isMobile ? '1 1 100%' : '0 0 323px' }}>
                        <BaseSelector
                            multiple
                            value={selectedDevices}
                            items={deviceItems}
                            label={t('source')}
                            onChange={handleDeviceChange}
                            onSelection={setDeviceSelection}
                            selectDisplayLabels={{
                                placeholderLabel: t('selectSource'),
                                countLabel: t('selectedWithValue')
                            }}
                        />
                    </Box>

                    {!isMobile && (
                        <WcnButton
                            variant="primary"
                            onClick={handleExport}
                            disabled={!currentFilter || filteredIncidents.length === 0}
                            isProgress={loading}
                            style={{
                                marginLeft: 'auto',
                                placeSelf: 'flex-end',
                                marginTop: '14px'
                            }}
                        >
                            {t('export')}
                        </WcnButton>
                    )}
                </Box>
            </>
        ),
        [isMobile, incidentTypeItems, selectedIncidentTypes, deviceItems, selectedDevices, t]
    );

    const mobilePanelContent = useMemo(() => (isMobile ? filterContent : null), [isMobile, filterContent]);

    useMobileHeaderPanelContent(mobilePanelContent, {
        title: t('filter'),
        tooltip: t('descriptions.incidentFilter')
    });

    return (
        <Box sx={{ width: '100%', bgcolor: 'background.default', pb: 3 }}>
            <NewPageTitleBar
                icon={<i className="bi bi-card-checklist" style={{ fontSize: '16px' }} />}
                title={t('events')}
                iconSize="18px"
                menus={getMenuData()}
                currentRoute={{ path: '/statistics/events' }}
                t={t}
                lastUpdate={lastUpdate}
            />

            <Container maxWidth={false}>
                {!isMobile && <Box sx={{ mb: 4, mt: 1 }}>{filterContent}</Box>}
                {!loading && (
                    <Box sx={{ mt: 2 }}>
                        <NewWcnTable
                            key={filterKey}
                            columns={tableColumns}
                            data={filteredIncidents}
                            loading={loading}
                            noDataMessage={t('noData')}
                            sorting={{ defaultSortBy: 'incidentTimestamp', defaultSortDirection: 'desc' }}
                            pagination={{
                                count: filteredIncidents.length,
                                rowsPerPage: 10,
                                rowsPerPageOptions: [10, 15, 20]
                            }}
                            mobileView="card"
                        />
                    </Box>
                )}

                {isMobile && (
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                        <WcnButton
                            variant="primary"
                            onClick={handleExport}
                            disabled={!currentFilter || filteredIncidents.length === 0}
                            isProgress={loading}
                        >
                            {t('export')}
                        </WcnButton>
                    </Box>
                )}
            </Container>

            {loading && <LoadingSpinner showBackground={false} />}
        </Box>
    );
};

export default ShopAutomationIncidents;
